import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { getAdminTeams, getAccessibleTeams } from "@/lib/api";
import { POPOVER_VIEWPORT_PAD } from "@/components/ui/popoverLayout";
import {
  unscopedKey,
  useViewerHostKey,
} from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type { AdminTeamRow, MeTeam } from "@/lib/types";

import { useScopeIdentity, useScopeSelection } from "./useScopeSelection";

type SwitcherOption = {
  id: string;
  name: string;
  membership: boolean;
  role?: string;
};

/** Header team switcher.
 *
 * Visible only when the signed-in user has 2+ team memberships, or is
 * a global admin. Posts `PUT /api/me/scope` with the selected team_id;
 * the backend resolves the season (no UI for season per PR #60).
 * Reconciles auth + scope stores and evicts every scoped cache entry
 * so subsequent reads issue against the new team.
 */
export function TeamSwitcher({
  testIdPrefix = "header-team-switcher",
  showSingleTeam = false,
  recipient = false,
  selection,
}: {
  /** Distinct per mount point — the scope BAR and the team-selection
   * GATE can render simultaneously, and duplicate testids break
   * strict-mode locators (#367). */
  testIdPrefix?: string;
  showSingleTeam?: boolean;
  /** Recipient choices are current-host memberships, never the admin override. */
  recipient?: boolean;
  selection?: ReturnType<typeof useScopeSelection>;
} = {}) {
  const me = useAuthStore((s) => s.me);
  const activeTeam = useScopeStore((s) => s.active?.team ?? null);
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const identity = useScopeIdentity();
  const ownedSelection = useScopeSelection();
  const { select, pendingId, error } = selection ?? ownedSelection;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const isGlobalAdmin = !recipient && !!me?.user?.is_global_admin;
  const directoryKey = useViewerHostKey("accessible-teams", { authority: identity.authority, generation: identity.generation });
  const directory = useQuery({
    queryKey: directoryKey,
    queryFn: async () => {
      const teams = await getAccessibleTeams();
      if (!identity.current()) throw new DOMException('Context changed', 'AbortError');
      return teams;
    },
    enabled: recipient && open && !!me?.user,
    staleTime: 0, retry: false, refetchOnMount: 'always',
  });
  const memberships = useMemo(() => {
    const teams = me?.teams ?? [];
    if (!recipient) return teams;
    if (!directory.isFetchedAfterMount || directory.isError || !identity.current()) return [];
    const allowed = new Set((directory.data ?? []).map(team => team.id));
    return teams.filter(team => allowed.has(team.id));
  }, [me?.teams, recipient, directory.data, directory.isFetchedAfterMount, directory.isError, identity]);
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-switcher"),
    queryFn: getAdminTeams,
    enabled: open && isGlobalAdmin,
    staleTime: 60_000,
  });

  const options = useMemo<SwitcherOption[]>(() => {
    const ownTeams: SwitcherOption[] = memberships.map((t: MeTeam) => ({
      id: t.id,
      name: t.name,
      membership: true,
      role: t.membership_role,
    }));
    if (!isGlobalAdmin) return ownTeams;
    const ownIds = new Set(ownTeams.map((o) => o.id));
    const adminTeams: SwitcherOption[] = (adminTeamsQuery.data ?? [])
      .filter((t: AdminTeamRow) => !ownIds.has(t.id))
      .map((t) => ({ id: t.id, name: t.name, membership: false }));
    return [...ownTeams, ...adminTeams];
  }, [memberships, isGlobalAdmin, adminTeamsQuery.data]);

  // Render-nothing rules.
  const visible =
    !!me?.user &&
    (recipient || memberships.length >= (showSingleTeam ? 1 : 2) || isGlobalAdmin);

  // Close on outside click, Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const el = containerRef.current;
      const popup = popupRef.current;
      const target = e.target as Node;
      if (el?.contains(target) || popup?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return;
    }

    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;

      const trigger = el.getBoundingClientRect();
      const viewportPad = POPOVER_VIEWPORT_PAD;
      const availableWidth = Math.max(0, window.innerWidth - viewportPad * 2);
      const width = Math.min(256, availableWidth);
      const maxLeft = window.innerWidth - width - viewportPad;
      const rightAlignedLeft = trigger.right - width;

      setPopupPos({
        top: trigger.bottom + 4,
        left: Math.max(viewportPad, Math.min(rightAlignedLeft, maxLeft)),
        width,
      });
    };

    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  if (!visible) return null;

  const triggerLabel =
    selectionRequired || !activeTeam ? "Select team" : activeTeam.name;

  const onSelect = async (teamId: string) => {
    if (pendingId) return;
    if (activeTeam?.id === teamId) {
      setOpen(false);
      return;
    }
    if (!options.some(option => option.id === teamId)) return;
    if (await select({ team_id: teamId })) setOpen(false);
  };

  const membershipOptions = options.filter((o) => o.membership);
  const adminOnlyOptions = options.filter((o) => !o.membership);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        disabled={!!pendingId}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testIdPrefix}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-full items-center gap-1.5 rounded border border-accent bg-accent px-2.5 py-1 font-medium text-accent-ink transition-colors hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <span className="min-w-0 truncate font-display uppercase tracking-wider text-[0.68rem]">
          {triggerLabel}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={2.2}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && popupPos && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={popupRef}
              role="listbox"
              data-tktic-popover={
                document.documentElement.dataset.ui === "approved-v5"
                  ? "select"
                  : undefined
              }
              aria-label="Switch team"
              data-testid={`${testIdPrefix}-panel`}
              style={{
                position: "fixed",
                top: popupPos.top,
                left: popupPos.left,
                width: popupPos.width,
              }}
              className="z-[200] max-h-[min(60dvh,28rem)] overflow-y-auto rounded border border-rule bg-surface shadow-pop"
            >
              {membershipOptions.length > 0 ? (
                <div data-testid={`${testIdPrefix}-group-memberships`}>
                  {isGlobalAdmin && adminOnlyOptions.length > 0 ? (
                    <div className="px-3 pt-2 pb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                      Your teams
                    </div>
                  ) : null}
                  {membershipOptions.map((o) => (
                    <OptionRow
                      key={o.id}
                      option={o}
                      active={o.id === activeTeam?.id}
                      pending={o.id === pendingId}
                      blocked={!!pendingId}
                      onSelect={onSelect}
                      testIdPrefix={testIdPrefix}
                    />
                  ))}
                </div>
              ) : null}

              {isGlobalAdmin && adminOnlyOptions.length > 0 ? (
                <div data-testid={`${testIdPrefix}-group-admin`}>
                  <div className="px-3 pt-2 pb-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                    {membershipOptions.length > 0 ? "All teams" : "Teams"}
                  </div>
                  {adminOnlyOptions.map((o) => (
                    <OptionRow
                      key={o.id}
                      option={o}
                      active={o.id === activeTeam?.id}
                      pending={o.id === pendingId}
                      blocked={!!pendingId}
                      onSelect={onSelect}
                      testIdPrefix={testIdPrefix}
                    />
                  ))}
                </div>
              ) : null}

              {isGlobalAdmin && adminTeamsQuery.isLoading ? (
                <div className="px-3 py-2 text-xs text-ink-muted">
                  Loading teams…
                </div>
              ) : null}

              {recipient && directory.isLoading ? <p className="px-3 py-2 text-xs text-ink-muted">Loading teams…</p> : null}
              {recipient && directory.isError ? <p role="alert" className="px-3 py-2 text-xs text-destructive">Teams could not be loaded. Close and reopen to retry.</p> : null}
              {options.length === 0 && !adminTeamsQuery.isLoading && (!recipient || (!directory.isLoading && !directory.isError)) ? (
                isGlobalAdmin ? (
                  <div className="px-3 py-2 text-xs text-ink-muted">
                    No teams yet.{" "}
                    <a href="/admin/teams" className="underline">
                      Create one
                    </a>
                    .
                  </div>
                ) : (
                  <div className="px-3 py-2 text-xs text-ink-muted">
                    No teams available.
                  </div>
                )
              ) : null}

              {error ? (
                <div
                  data-testid={`${testIdPrefix}-error`}
                  className="border-t border-rule px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function OptionRow({
  option,
  active,
  pending,
  blocked = false,
  onSelect,
  testIdPrefix,
}: {
  option: SwitcherOption;
  active: boolean;
  pending: boolean;
  blocked?: boolean;
  onSelect: (id: string) => void | Promise<void>;
  testIdPrefix: string;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      disabled={pending || blocked}
      onClick={() => void onSelect(option.id)}
      data-testid={`${testIdPrefix}-option-${option.id}`}
      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-field disabled:opacity-50 ${
        active ? "bg-accent text-accent-ink" : ""
      }`}
    >
      <span
        className={`min-w-0 truncate ${active ? "text-accent-ink" : "text-ink"}`}
      >
        {option.name}
      </span>
      <span
        className={`flex shrink-0 items-center gap-2 text-[0.6rem] font-display uppercase tracking-wider ${
          active ? "text-accent-ink" : "text-ink-muted"
        }`}
      >
        {option.role ? <span>{option.role.replace(/_/g, " ")}</span> : null}
        {active ? (
          <>
            <Check size={13} strokeWidth={2.4} aria-hidden="true" />
            <span className="sr-only">active</span>
          </>
        ) : null}
        {pending ? <span>…</span> : null}
      </span>
    </button>
  );
}
